import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { packageService } from '../../services/package.service';
import { perksService } from '../../services/perks.service';
import { storeService } from '../../services/store.service';
import './MenuOverlay.css';

interface UserProfile {
  name: string;
  email: string;
  picture: string;
}

interface MenuOverlayProps {
  onClose: () => void;
  onBooking: () => void;
  user: UserProfile | null | undefined;
  onLogin: (profile: UserProfile) => void;
  onLogout: () => void;
  onLoginError: (error: any) => void;
  selectedStore?: any;
  onLoginClick?: () => void;
}

const MenuOverlay: React.FC<MenuOverlayProps> = ({
  onClose,
  onBooking,
  user,
  onLogin,
  onLogout,
  onLoginError,
  selectedStore,
  onLoginClick,
}) => {
  const navigate = useNavigate();
  // Sessions the client can still book with. Shown on the menu item so a
  // client who's forgotten they have a pack is reminded at the moment they
  // go looking for something to do — which is the whole point of surfacing
  // it here rather than only on the page itself.
  const [usablePacks, setUsablePacks] = useState(0);
  // Perks ready to claim or use — same idea as the packs badge. Hidden
  // entirely when this groomer has switched MPG Perks off.
  const [readyPerks, setReadyPerks] = useState(0);
  const perksEnabled = storeService.getPerksEnabled();

  useEffect(() => {
    if (!user) {
      setUsablePacks(0);
      return;
    }
    let cancelled = false;
    packageService.countUsablePackages().then((count) => {
      if (!cancelled) setUsablePacks(count);
    });
    if (perksEnabled) {
      perksService.countReadyPerks().then((count) => {
        if (!cancelled) setReadyPerks(count);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [user, perksEnabled]);

  const handleEditProfile = () => {
    navigate('/profile/edit');
    onClose();
  };

  return (
    <div className="menu-overlay">
      {/* Background graphic - mobile and desktop versions */}
      <div className="menu-background-graphic">
        <img src="/assets/menu_lines_m.svg" alt="" aria-hidden="true" className="bg-mobile" />
        <img src="/assets/menu_lines_d.svg" alt="" aria-hidden="true" className="bg-desktop" />
      </div>
      
      <div className="menu-header">
        <button className="icon-button opacity-0">
          <img src="/assets/menu.svg" alt="Menu" />
        </button>
        
        <button className="icon-button" onClick={onClose}>
          <img src="/assets/close.svg" alt="Close" />
        </button>
      </div>
      
      <div className="menu-content">
        <nav className="menu-nav">
          <button className="menu-button" onClick={() => { 
            navigate('/');
            onClose(); 
          }}>
            <span>Home</span>
          </button>
          <button className="menu-button" onClick={onBooking}>
            <span>Book now</span>
          </button>
          <button className="menu-button" onClick={() => { 
            navigate('/appointments');
            onClose(); 
          }}>
            <span>Appointments</span>
          </button>
          {/* Packages — only meaningful once signed in. */}
          {user && (
            <button className="menu-button" onClick={() => {
              navigate('/profile/packages');
              onClose();
            }}>
              <span>Packages</span>
              {usablePacks > 0 && (
                <span className="menu-badge">{usablePacks}</span>
              )}
            </button>
          )}
          {/* MPG Perks — a benefit of booking through MPG, so it sits with
              the customer's own things. Signed-out visitors meet the login
              gate on the page itself. */}
          {perksEnabled && (
            <button className="menu-button" onClick={() => {
              navigate('/my-perks');
              onClose();
            }}>
              <span>MPG Perks</span>
              {readyPerks > 0 && (
                <span className="menu-badge">{readyPerks}</span>
              )}
            </button>
          )}
          {/* Salon promotions: the business's offers, and the client's own. */}
          <button className="menu-button" onClick={() => {
            navigate('/promotions');
            onClose();
          }}>
            <span>Promotions</span>
          </button>
          {/* Profile button - only show when logged in */}
          {user && (
            <button className="menu-button" onClick={handleEditProfile}>
              <img src="/assets/user.svg" alt="Profile" />
              <span>Profile</span>
            </button>
          )}
          <button className="menu-button">
            <img src="/assets/location.svg" alt="Location" />
            <span>{selectedStore?.name || 'Select location'}</span>
          </button>
          {user ? (
            <button className="menu-button" onClick={onLogout}>
              <img src="/assets/login.svg" alt="Logout" />
              <span>Sign out</span>
            </button>
          ) : (
            <button
              type="button"
              className="menu-button"
              onClick={() => { onClose(); onLoginClick?.(); }}
            >
              <img src="/assets/login.svg" alt="Login" />
              <span>Log in</span>
            </button>
          )}
        </nav>
      </div>
      
      <div className="menu-footer">
        <div className="menu-tagline">
          <p>A better experience for you and your pet</p>
        </div>

        <a href="https://www.mypetgroomer.com.au" target="_blank" rel="noopener noreferrer" className="powered-by-badge">
          <span className="powered-by-text">Powered by</span>
          <img
            src="/assets/Logo_light.svg"
            alt="My Pet Groomer"
            className="powered-by-logo"
          />
          <span className="powered-by-reg">®</span>
        </a>
      </div>
    </div>
  );
};

export default MenuOverlay; 